import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, Button, Alert, Typography, Space, Tag } from 'antd';
import { EditOutlined, UndoOutlined } from '@ant-design/icons';
import { useAssetActions } from '../../../../hooks/useAssets';
import { Asset, AssetStatus, UpdateAssetModalProps } from '../../../../types/asset.types';

const { Option } = Select;
const { Text } = Typography;

// UpdateAssetModalProps artık types'tan import ediliyor

const UpdateAssetModal: React.FC<UpdateAssetModalProps> = ({
  visible,
  onClose,
  onSuccess,
  asset
}) => {
  const [form] = Form.useForm();
  const { updateAsset, returnAsset, isLoading } = useAssetActions();
  const [actionType, setActionType] = useState<'status' | 'return'>('status');

  useEffect(() => {
    if (visible && asset) {
      form.setFieldsValue({
        status: asset.status
      });
    }
  }, [visible, asset, form]);

  const handleSubmit = async (values: any) => {
    if (!asset) return;
    
    try {
      if (actionType === 'return') {
        await returnAsset(asset.id);
      } else {
        // updateAsset kullanarak sadece durum güncelleme
        await updateAsset(asset.id, {
          name: asset.name,
          serialNumber: asset.serialNumber,
          status: values.status
        });
      }
      onSuccess();
      form.resetFields();
    } catch (error) {
      console.error('Update asset failed:', error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const getStatusText = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'Envanterde';
      case AssetStatus.ASSIGNED:
        return 'Atanmış';
      case AssetStatus.SCRAPPED:
        return 'Kullanım Dışı';
      default:
        return status;
    }
  };

  const getStatusColor = (status: AssetStatus) => {
    switch (status) {
      case AssetStatus.IN_STOCK:
        return 'success';
      case AssetStatus.ASSIGNED:
        return 'processing';
      case AssetStatus.SCRAPPED:
        return 'error';
      default:
        return 'default';
    }
  };

  if (!asset) return null;

  return (
    <Modal
      title={
        <div className="d-flex align-items-center">
          <div className="bg-warning rounded-circle p-2 d-flex align-items-center justify-content-center me-3">
            <EditOutlined className="text-white" style={{ fontSize: '18px' }} />
          </div>
          <div>
            <h5 className="mb-0">Zimmet İşlemleri</h5>
            <Text type="secondary" className="small">Zimmet durumunu güncelle veya iade al</Text>
          </div>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      width={550}
      footer={null}
      destroyOnClose
    >
      <div className="update-asset-form">
        {/* Zimmet Bilgisi */}
        <Alert
          message={
            <div>
              <div className="d-flex align-items-center justify-content-between mb-2">
                <div className="d-flex align-items-center">
                  <i className="ti ti-archive me-2 text-primary"></i>
                  <strong>{asset.name}</strong>
                </div>
                <Tag color={getStatusColor(asset.status)}>
                  {getStatusText(asset.status)}
                </Tag>
              </div>
              <div className="d-flex justify-content-between">
                <Text type="secondary">Seri No: {asset.serialNumber}</Text>
                {asset.user && (
                  <Text type="secondary">
                    Atanan: {asset.user.firstName} {asset.user.lastName}
                  </Text>
                )}
              </div>
            </div>
          }
          type="info"
          showIcon={false}
          className="mb-4"
        />

        {/* İşlem Seçimi */}
        {/* <div className="action-selection mb-4">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text strong>İşlem Türü Seçin:</Text>
            <Space>
              <Button
                type={actionType === 'status' ? 'primary' : 'default'}
                onClick={() => setActionType('status')}
                icon={<EditOutlined />}
              >
                Durum Güncelle
              </Button>
              {asset.status === AssetStatus.ASSIGNED && (
                <Button
                  type={actionType === 'return' ? 'primary' : 'default'}
                  onClick={() => setActionType('return')}
                  icon={<UndoOutlined />}
                >
                  İade Al
                </Button>
              )}
            </Space>
          </Space>
        </div> */}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          {actionType === 'status' ? (
            <Form.Item
              name="status"
              label="Yeni Durum"
              rules={[
                { required: true, message: 'Durum seçimi gereklidir' }
              ]}
            >
              <Select placeholder="Durum seçin" size="large">
                <Option value={AssetStatus.IN_STOCK}>
                  <Tag color="success">Envanterde</Tag>
                </Option>
                <Option value={AssetStatus.SCRAPPED}>
                  <Tag color="error">Kullanım Dışı</Tag>
                </Option>
              </Select>
            </Form.Item>
          ) : (
            <Alert
              message="İade İşlemi"
              description="Bu zimmet çalışandan geri alınacak ve envantere döndürülecektir."
              type="warning"
              showIcon
              className="mb-3"
            />
          )}

          <Form.Item className="mb-0">
            <div className="d-flex justify-content-end gap-2">
              <Button onClick={handleCancel} size="large">
                İptal
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={isLoading}
                size="large"
                icon={actionType === 'return' ? <UndoOutlined /> : <EditOutlined />}
              >
                {actionType === 'return' ? 'İade Al' : 'Güncelle'}
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>

      <style>{`
        .update-asset-form .ant-form-item-label > label {
          font-weight: 600;
        }
        
        .action-selection {
          border-radius: 8px;
          padding: 16px;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
        }
      `}</style>
    </Modal>
  );
};

export default UpdateAssetModal; 